import { useState, useEffect, useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { selectBusinessInventoryList } from 'redux/selectors';
import {
  updateProductUseInventory,
  updateTrackInventory,
} from 'redux/actions/inventory/inventory';
import {
  UOM_OPTIONS,
  COUNT_TYPE,
} from 'components/MenuNew/FormView/Inventory/configs';
import { MenuItem } from '@mui/material';

// Validation schema for inventory form
const inventorySchema = yup.object().shape({
  sku: yup.string(),

  unit: yup.string(),
  stock: yup.number(), // Allows negative integers

  barcodeNumber: yup
    .array()
    .of(yup.string().max(50, 'Barcode cannot exceed 50 characters'))
    .nullable(),
  uuid: yup.string(),
  trackInventory: yup.boolean(),
  useInventory: yup.boolean(),
});

// Default values for the form
const defaultValues = {
  sku: '',
  unit: '',
  stock: 0,
  barcodeNumber: [],
  uuid: '',
  trackInventory: false,
  useInventory: false,
};
export const UPDATE_INVENTORY_TYPE = {
  SKU: 'sku',
  UNIT: 'unit',
  STOCK: 'stock',
  BARCODE_NUMBER: 'barcodeNumber',
};

const useInventoryForm = (productFormMethods) => {
  const dispatch = useDispatch();
  const productId = productFormMethods.getValues()?._id;
  const [currentType, setCurrentType] = useState('');
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedInventory, setSelectedInventory] = useState({
    type: '',
    data: {},
    action: null,
    title: '',
  });

  const businessInventoryList = useSelector(selectBusinessInventoryList);

  // Reactively subscribe to the parent product form so this hook re-runs when
  // a different item is selected. `getValues()` is NOT reactive: switching items
  // calls `reset()` on the parent form (in a useEffect) without changing the
  // form reference, so a memoized child never re-renders and the sync below
  // would otherwise keep showing the previously selected item's inventory.
  const watchedInventory = useWatch({
    control: productFormMethods.control,
    name: 'inventory',
  });
  const watchedUseInventory = useWatch({
    control: productFormMethods.control,
    name: 'useInventory',
  });

  const handleClose = () => {
    setOpenUpdateModal(false);
    setCurrentType('');
    setSelectedInventory({
      type: '',
      data: {},
      action: null,
    });
  };

  // Initialize React Hook Form
  const inventoryFormMethods = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(inventorySchema),
    mode: 'all',
  });

  const isDisabled = inventoryFormMethods?.getValues('useInventory');

  const { control } = inventoryFormMethods;

  useEffect(() => {
    dispatch({
      type: 'SET_IS_DISABLED',
      payload: isDisabled,
    });
  }, [isDisabled]);

  useEffect(() => {
    const inventory = watchedInventory;
    const inventoryItems = inventory?.reduce((result, item) => {
      if (result) return result; // Already found, skip

      if (!item.isMain) return result;

      const businessInventory = businessInventoryList?.find(
        (business) => business.uuid === item.uuid
      );

      if (businessInventory) {
        return { ...businessInventory };
      }

      return result;
    }, null);

    inventoryFormMethods.reset({
      sku: inventoryItems?.sku || '',
      unit: inventoryItems?.unit || '',
      stock: inventoryItems?.stock || 0,
      barcodeNumber: inventoryItems?.barcodeNumber || [],
      uuid: inventoryItems?.uuid || '',
      trackInventory: inventoryItems?.trackInventory ?? false,
      useInventory: !watchedUseInventory,
    });
    // Re-sync whenever the global inventory list OR the selected item changes.
    // `watchedInventory`/`watchedUseInventory` come from `useWatch`, so this
    // effect re-runs after the parent form is reset to a different item.
  }, [businessInventoryList, watchedInventory, watchedUseInventory]);

  const handleSelectInventory = (type, action = null, title = '') => {
    const data = inventoryFormMethods.getValues(type);
    setSelectedInventory({
      type,
      data,
      action,
      title,
    });
    setCurrentType(action);
    setOpenUpdateModal(true);
  };

  const handleSubmit = (values, event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTrackInventory = (event) => {
    const { checked } = event.target;
    inventoryFormMethods.setValue('trackInventory', checked);
    try {
      dispatch(
        updateTrackInventory({
          uuid: inventoryFormMethods?.getValues('uuid'),
          trackInventory: checked,
        })
      );
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleUseInventory = (event) => {
    const { checked } = event.target;
    inventoryFormMethods.setValue('useInventory', checked);
    try {
      dispatch(
        updateProductUseInventory(productId, { useInventory: !checked })
      );
    } catch (error) {
      console.log(error, '>>>');
    }
  };

  // Memoized event handlers to prevent unnecessary re-renders
  const handleSkuClick = useCallback(() => {
    handleSelectInventory(UPDATE_INVENTORY_TYPE.SKU, null, 'Update SKU code');
  }, [handleSelectInventory]);

  const handleUnitClick = useCallback(() => {
    handleSelectInventory(
      UPDATE_INVENTORY_TYPE.UNIT,
      null,
      'Update Unit of Measure'
    );
  }, [handleSelectInventory]);

  const handleBarcodeClick = useCallback(() => {
    handleSelectInventory(
      UPDATE_INVENTORY_TYPE.BARCODE_NUMBER,
      null,
      'Update Barcodes'
    );
  }, [handleSelectInventory]);

  const handleStockRemove = useCallback(() => {
    handleSelectInventory(
      UPDATE_INVENTORY_TYPE.STOCK,
      COUNT_TYPE.REMOVE,
      'Remove Stock'
    );
  }, [handleSelectInventory]);

  const handleStockAdd = useCallback(() => {
    handleSelectInventory(
      UPDATE_INVENTORY_TYPE.STOCK,
      COUNT_TYPE.ADD,
      'Add Stock'
    );
  }, [handleSelectInventory]);

  const handleStockAdjust = useCallback(() => {
    handleSelectInventory(
      UPDATE_INVENTORY_TYPE.STOCK,
      COUNT_TYPE.ADJUST,
      'Adjust Stock'
    );
  }, [handleSelectInventory]);

  // Memoized UOM options to prevent recreation on every render
  const uomMenuItems = useMemo(() => {
    return UOM_OPTIONS.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ));
  }, []);

  return {
    inventoryFormMethods,
    control,
    handleSubmit,
    openUpdateModal,
    handleClose,
    currentType,
    handleSelectInventory,
    selectedInventory,
    handleTrackInventory,
    handleUseInventory,
    isDisabled,
    handleSkuClick,
    handleUnitClick,
    handleBarcodeClick,
    handleStockRemove,
    handleStockAdd,
    handleStockAdjust,
    uomMenuItems,
  };
};

export default useInventoryForm;
